import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { setVisibleColumns, setSortOrder, setSortBy, setFilterField } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";

const TIME_SORT_FIELDS = {
  hourCommencing: "hourCommencing",
  transactionQty: "transactionQty",
  salesIncTax: "salesIncTax",
  netSalesIncTax: "netSalesIncTax",
};

const TIME_SORT_LABELS = {
  [TIME_SORT_FIELDS.hourCommencing]: "Hour Commencing",
  [TIME_SORT_FIELDS.transactionQty]: "Number of Orders",
  [TIME_SORT_FIELDS.salesIncTax]: "Sales Inc. Tax",
  [TIME_SORT_FIELDS.netSalesIncTax]: "Net Sales Inc. Tax",
};

/** HOUR COMMENCING: weekday + full month name, 24h time, e.g. Monday, 6 April 2026 13:00 */
function formatHourCommencing(value) {
  if (value == null || value === "") return "";
  const v = typeof value === "string" ? value.trim() : value;
  let m;
  if (typeof v === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}/.test(v)) {
    m = moment(v, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "DD/MM/YYYY"], true);
  } else {
    m = moment(v);
  }
  return m.isValid() ? m.format("dddd, D MMMM YYYY HH:mm") : "";
}

const SalesBreakdownByTime = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const salesFetchSeqRef = useRef(0);

  // Date range state - now handled by Redux

  // Reporting categories state
  const [reportingCategories, setReportingCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Additional dropdown states
  const [locations, setLocations] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // Column selection and preferences
  const pageKey = "salesBreakdownByTime";
  // Default: show only core columns; rest via Column Selection
  const defaultVisibleColumns = {
    transactionQty: true,
    refundQty: true,
    refundValue: true,
    itemQty: true,
    salesIncTax: true,
    discountsPromotions: false,
    avgTransactionNetSales: false,
    netSalesIncTax: true,
    netSalesExcTax: false
  };

  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "hourCommencing", label: "Hour Commencing" };
  const defaultPageSize = { value: "20", label: "20" };
  const validPageSizes = ["20", "50", "100"];
  
  // Default filter values
  const defaultLocation = { value: "all", label: "All Locations" };
  const defaultStaff = { value: "all", label: "All Staff" };
  const defaultTimeInterval = { value: "hourly", label: "Hourly" };
  const defaultDevice = { value: "all", label: "All Devices" };
  const defaultReportingCategory = { value: "all", label: "All Categories" };
  
  // Default to "This Week" if no existing filter
  const defaultDateRange = useMemo(
    () => [
      moment().startOf("week").toDate(),
      moment().endOf("week").toDate(),
    ],
    []
  );

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  
  // Get filter values from Redux
  const location = preferences.location || defaultLocation;
  const staff = preferences.staff || defaultStaff;
  const timeInterval = preferences.timeInterval || defaultTimeInterval;
  const device = preferences.device || defaultDevice;
  const reportingCategory = preferences.reportingCategory || defaultReportingCategory;

  // Prefer stored range only when dates are valid; cleared/[null,null] must fall back
  const [startDate, endDate] = useMemo(() => {
    const range = preferences.dateRange;
    if (!Array.isArray(range)) return defaultDateRange;
    const start =
      range[0] != null && moment(range[0]).isValid()
        ? moment(range[0]).toDate()
        : null;
    const end =
      range[1] != null && moment(range[1]).isValid()
        ? moment(range[1]).toDate()
        : null;
    if (!start) return defaultDateRange;
    return [start, end || moment(start).endOf("day").toDate()];
  }, [preferences.dateRange, defaultDateRange]);
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: TIME_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  // Currency symbols mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Function to get theme color
  // const getThemeColor = () => {
  //   const colorMap = {
  //     'red': '#dc3545',
  //     'pink': '#e91e63',
  //     'orange': '#fd7e14',
  //     'yellow': '#ffc107',
  //     'lime': '#84cc16',
  //     'green': '#198754',
  //     'teal': '#20c997',
  //     'cyan': '#0dcaf0',
  //     'blue': '#0d6efd',
  //     'purple': '#6f42c1',
  //     'indigo': '#6610f2',
  //     'dark': '#212529'
  //   };
  //   return colorMap[appTheme] || '#20c997'; // Default to teal
  // };

  

  // Filter options - Will be populated from API
  const locationOptions = locations.length > 0 ? locations : [
    { value: "all", label: "All Locations" }
  ];

  const staffOptions = staffData.length > 0 ? staffData : [
    { value: "all", label: "All Staff" }
  ];

  const timeIntervalOptions = [
    { value: "hourly", label: "Hourly" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" }
  ];

  const deviceOptions = devices.length > 0 ? devices : [
    { value: "all", label: "All Devices" }
  ];

  // Reporting categories options will be populated from API
  const reportingCategoryOptions = reportingCategories.length > 0 ? reportingCategories : [
    { value: "all", label: "All Categories" }
  ];

  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey, field: filterName, value: selectedOption }));
  };

  const clearFilters = () => {
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "staff", value: defaultStaff }));
    dispatch(setFilterField({ pageKey, field: "timeInterval", value: defaultTimeInterval }));
    dispatch(setFilterField({ pageKey, field: "device", value: defaultDevice }));
    dispatch(setFilterField({ pageKey, field: "reportingCategory", value: defaultReportingCategory }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    setSearch("");
  };

  const hasFiltersApplied = () =>
    location?.value !== "all" ||
    staff?.value !== "all" ||
    device?.value !== "all" ||
    reportingCategory?.value !== "all" ||
    timeInterval?.value !== defaultTimeInterval.value ||
    Boolean(search?.trim());

  const applyFilters = useCallback(async () => {
    if (!CompID) {
      setInitialLoadComplete(true);
      return;
    }

    const fetchSeq = ++salesFetchSeqRef.current;
    setLoading(true);
    try {
      const resolvedStart = startDate && moment(startDate).isValid()
        ? moment(startDate).startOf("day")
        : moment().startOf("week");
      const resolvedEnd = endDate && moment(endDate).isValid()
        ? moment(endDate).endOf("day")
        : startDate && moment(startDate).isValid()
          ? moment(startDate).endOf("day")
          : moment().endOf("week");

      const params = {
        CompID,
        startDate: resolvedStart.format("YYYY-MM-DD"),
        endDate: resolvedEnd.format("YYYY-MM-DD"),
        timeInterval: timeInterval?.value || "hourly",
        sortBy: sortBy?.value || "hourCommencing",
        sortOrder: sortOrder?.value || "DESC",
        pageNumber,
        pageSize: Number(pageSize?.value) || 20,
      };

      if (location?.value && location.value !== "all") {
        params.locationFilter = location.value;
      }
      if (staff?.value && staff.value !== "all") {
        params.staffFilter = staff.value;
      }
      if (device?.value && device.value !== "" && device.value !== "all") {
        params.deviceFilter = String(device.value).trim();
      }
      if (search?.trim()) {
        params.searchTerm = search.trim();
      }

      const response = await axiosInstance.get(APIs.GET_SALES_BREAKDOWN_BY_TIME, {
        params,
        timeout: 60000,
      });

      if (fetchSeq !== salesFetchSeqRef.current) return;

      if (response.data?.status) {
        const responseData = response.data.data;
        const salesDataRaw = responseData?.SalesData || responseData?.salesData || responseData || [];
        const totalCount = responseData?.TotalCount || responseData?.totalCount || 0;
        const apiTotalPages = responseData?.TotalPages || responseData?.totalPages || 0;
        
        // Normalize property names to camelCase (in case backend sends PascalCase)
        const salesData = Array.isArray(salesDataRaw) ? salesDataRaw.map(item => ({
          hourCommencing: item.hourCommencing || item.HourCommencing,
          transactionQty: item.transactionQty || item.TransactionQty || 0,
          refundQty: item.refundQty || item.RefundQty || 0,
          refundValue: item.refundValue || item.RefundValue || 0,
          itemQty: item.itemQty || item.ItemQty || 0,
          salesIncTax: item.salesIncTax || item.SalesIncTax || 0,
          discountsPromotions: item.discountsPromotions || item.DiscountsPromotions || 0,
          avgTransactionNetSales: item.avgTransactionNetSales || item.AvgTransactionNetSales || 0,
          netSalesIncTax: item.netSalesIncTax || item.NetSalesIncTax || 0,
          netSalesExcTax: item.netSalesExcTax || item.NetSalesExcTax || 0
        })) : [];
        
        setSalesData(salesData);
        setTotalRecords(totalCount);
        setTotalPages(
          apiTotalPages || Math.ceil(totalCount / (Number(pageSize?.value) || 20)) || 1
        );
      } else {
        const message =
          response.data?.message ||
          response.data?.Message ||
          "Error loading sales data";
        showErrorToast(message);
        setSalesData([]);
        setTotalRecords(0);
        setTotalPages(0);
      }
    } catch (error) {
      if (fetchSeq !== salesFetchSeqRef.current) return;
      console.error("Error fetching sales data:", error);
      const message =
        error?.code === "ECONNABORTED"
          ? "Sales report timed out. Try a smaller date range."
          : error?.response?.data?.message ||
            error?.response?.data?.Message ||
            error?.message ||
            "Error loading sales data";
      showErrorToast(message);
      setSalesData([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      if (fetchSeq === salesFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [
    CompID,
    startDate,
    endDate,
    location,
    staff,
    timeInterval,
    device,
    search,
    sortBy,
    sortOrder,
    pageNumber,
    pageSize,
  ]);

  // CSV Export function
  const exportToCSV = () => {
    const csvContent = [
      // Headers
      "Hour Commencing,Number of Orders,Refund Qty,Refund Value,Item Qty,Sales Inc. Tax,Discounts & Promotions,Average Transaction Net Sales,Net Sales Inc. Tax,Net Sales Exc. Tax",
      // Data rows
      ...salesData.map(row =>
        `${formatHourCommencing(row.hourCommencing)},${row.transactionQty},${row.refundQty},${row.refundValue},${row.itemQty},${row.salesIncTax},${row.discountsPromotions},${row.avgTransactionNetSales},${row.netSalesIncTax},${row.netSalesExcTax}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sales-breakdown-by-time.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    showSuccessToast("CSV file exported successfully");
  };

  // Export to Word
  const exportToWord = () => {
    const tableData = salesData.map(row => [
      formatHourCommencing(row.hourCommencing),
      row.transactionQty,
      row.refundQty,
      `${getCurrencySymbol(compCurrency)} ${row.refundValue.toFixed(2)}`,
      row.itemQty,
      `${getCurrencySymbol(compCurrency)} ${row.salesIncTax.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.discountsPromotions.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.avgTransactionNetSales.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.netSalesIncTax.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.netSalesExcTax.toFixed(2)}`
    ]);

    const htmlContent = `
      <html>
        <head>
          <title>Sales Breakdown by Time Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h1>Sales Breakdown by Time Report</h1>
          <table>
            <thead>
              <tr>
                <th>HOUR COMMENCING</th>
                <th>NUMBER OF ORDERS</th>
                <th>REFUND QTY</th>
                <th>REFUND VALUE</th>
                <th>ITEM QTY</th>
                <th>SALES INC. TAX</th>
                <th>DISCOUNTS & PROMOTIONS</th>
                <th>AVERAGE TRANSACTION NET SALES</th>
                <th>NET SALES INC. TAX</th>
                <th>NET SALES EXC. TAX</th>
              </tr>
            </thead>
            <tbody>
              ${tableData.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "sales_breakdown_by_time.docx");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccessToast("Word document exported successfully!");
  };

  // Export to Excel
  const exportToExcel = () => {
    const csvContent = [
      // Excel Header
      ["HOUR COMMENCING", "NUMBER OF ORDERS", "REFUND QTY", "REFUND VALUE", "ITEM QTY", "SALES INC. TAX", "DISCOUNTS & PROMOTIONS", "AVERAGE TRANSACTION NET SALES", "NET SALES INC. TAX", "NET SALES EXC. TAX"],
      // Excel Data
      ...salesData.map(row => [
        formatHourCommencing(row.hourCommencing),
        row.transactionQty,
        row.refundQty,
        row.refundValue.toFixed(2),
        row.itemQty,
        row.salesIncTax.toFixed(2),
        row.discountsPromotions.toFixed(2),
        row.avgTransactionNetSales.toFixed(2),
        row.netSalesIncTax.toFixed(2),
        row.netSalesExcTax.toFixed(2)
      ])
    ].map(row => row.join("\t")).join("\n");

    const blob = new Blob([csvContent], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "sales_breakdown_by_time.xls");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccessToast("Excel file exported successfully!");
  };

  // Print Report
  const printReport = () => {
    const printWindow = window.open('', '_blank');
    const tableData = salesData.map(row => [
      formatHourCommencing(row.hourCommencing),
      row.transactionQty,
      row.refundQty,
      `${getCurrencySymbol(compCurrency)} ${row.refundValue.toFixed(2)}`,
      row.itemQty,
      `${getCurrencySymbol(compCurrency)} ${row.salesIncTax.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.discountsPromotions.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.avgTransactionNetSales.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.netSalesIncTax.toFixed(2)}`,
      `${getCurrencySymbol(compCurrency)} ${row.netSalesExcTax.toFixed(2)}`
    ]);

    printWindow.document.write(`
      <html>
        <head>
          <title>Sales Breakdown by Time Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { border-collapse: collapse; width: 100%; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            h1 { color: #333; text-align: center; }
            @media print {
              body { margin: 0; }
              table { font-size: 12px; }
            }
          </style>
        </head>
        <body>
          <h1>Sales Breakdown by Time Report</h1>
          <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()}</p>
          <table>
            <thead>
              <tr>
                <th>HOUR COMMENCING</th>
                <th>NUMBER OF ORDERS</th>
                <th>REFUND QTY</th>
                <th>REFUND VALUE</th>
                <th>ITEM QTY</th>
                <th>SALES INC. TAX</th>
                <th>DISCOUNTS & PROMOTIONS</th>
                <th>AVERAGE TRANSACTION NET SALES</th>
                <th>NET SALES INC. TAX</th>
                <th>NET SALES EXC. TAX</th>
              </tr>
            </thead>
            <tbody>
              ${tableData.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();

    showSuccessToast("Report sent to printer!");
  };

  // Fetch reporting categories
  const fetchReportingCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CATEGORY_TYPES, {
        params: {
          CompID
        }
      });
      const categories = response.data.data || response.data || [];

      console.log(categories);
      // Transform categories to dropdown format
      const categoryOptions = [
        { value: "all", label: "All Categories" },
        ...categories.map(category => ({
          value: category.rC_ID || category.id,
          label: category.rC_Name || category.name
        }))
      ];

      setReportingCategories(categoryOptions);
    } catch (error) {
      console.error('Error fetching reporting categories:', error);
      showErrorToast("Error loading reporting categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch locations (company branches)
  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: {
          CompID
        }
      });
      const locationData = response.data.data || response.data || [];
      
      // Transform locations to dropdown format
      const locationOptions = [
        { value: "all", label: "All Locations" },
        ...locationData.map(location => ({
          value: location.CompID,
          label: location.CompName
        }))
      ];
      
      setLocations(locationOptions);
    } catch (error) {
      console.error('Error fetching locations:', error);
      showErrorToast("Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  };

  // Fetch staff
  const fetchStaff = async () => {
    try {
      setLoadingStaff(true);
      const response = await axiosInstance.get(APIs.GET_ALL_STAFF, {
        params: {
          CompID
        }
      });
      const staffData = response.data.data || response.data || [];

      // Transform staff to dropdown format
      const staffOptions = [
        { value: "all", label: "All Staff" },
        ...staffData.map(staffMember => ({
          value: staffMember.userName,
          label: `${staffMember.firstName || ''} ${staffMember.lastName || ''}`.trim()  
        }))
      ];

      setStaffData(staffOptions);
    } catch (error) {
      console.error('Error fetching staff:', error);
      showErrorToast("Error loading staff");
    } finally {
      setLoadingStaff(false);
    }
  };

  // Fetch devices
  const fetchDevices = async () => {
    try {
      setLoadingDevices(true);
      const response = await axiosInstance.get(APIs.GET_ALL_DEVICES, {
        params: {
          CompID
        }
      });
      const deviceData = response.data.data || response.data || [];

      // Transform devices to dropdown format
      const deviceOptions = [
        { value: "all", label: "All Devices" },
        ...deviceData.map(device => ({
          value: device.deviceID,
          label: device.deviceName
        }))
      ];

      setDevices(deviceOptions);
    } catch (error) {
      console.error('Error fetching devices:', error);
      showErrorToast("Error loading devices");
    } finally {
      setLoadingDevices(false);
    }
  };

  // Handle date range changes
  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = update;
    // Clear → restore default week instead of persisting [null, null]
    if (!newStartDate && !newEndDate) {
      dispatch(
        setFilterField({
          pageKey,
          field: "dateRange",
          value: defaultDateRange,
        })
      );
      return;
    }
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      dispatch(
        setFilterField({
          pageKey,
          field: "dateRange",
          value: [newStartDate, newEndDate],
        })
      );
    } else {
      showSuccessToast("Invalid date selected");
    }
  };

  // Initialize preferences
  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    
    // Initialize filter fields
    if (!preferences.location) {
      dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    }
    if (!preferences.staff) {
      dispatch(setFilterField({ pageKey, field: "staff", value: defaultStaff }));
    }
    if (!preferences.timeInterval) {
      dispatch(setFilterField({ pageKey, field: "timeInterval", value: defaultTimeInterval }));
    }
    if (!preferences.device) {
      dispatch(setFilterField({ pageKey, field: "device", value: defaultDevice }));
    }
    if (!preferences.reportingCategory) {
      dispatch(setFilterField({ pageKey, field: "reportingCategory", value: defaultReportingCategory }));
    }
    const storedRange = preferences.dateRange;
    const storedStartValid =
      Array.isArray(storedRange) &&
      storedRange[0] != null &&
      moment(storedRange[0]).isValid();
    if (!storedStartValid) {
      dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    timeInterval?.value,
    location?.value,
    staff?.value,
    device?.value,
    reportingCategory?.value,
    startDate,
    endDate,
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => applyFilters(), [applyFilters]);

  // Fetch all dropdown data on component mount
  useEffect(() => {
    fetchReportingCategories();
    fetchLocations();
    fetchStaff();
    fetchDevices();
  }, []);

  const renderNoDataMessage = () => {
    if (loading || salesData.length > 0) return null;
    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No sales data found"
        description="Try adjusting your filters or date range."
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="#/report">Report</a>
            </li>
            <li className="breadcrumb-item active">Sales Breakdown by Time</li>
          </ol>
          <h1 className="page-header mb-0">Sales Breakdown by Time</h1>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {initialLoadComplete && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Sales Breakdown by Time"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search report data..."
            bodyFlush
            topFilters={
              <>
                <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                  <DateRangePicker
                    startDate={startDate && moment(startDate).isValid() ? startDate : null}
                    endDate={endDate && moment(endDate).isValid() ? endDate : null}
                    onChange={handleDateRangeChange}
                    placeholder="Select Date Range"
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-clock">
                  <Select
                    value={timeInterval}
                    onChange={(option) => handleFilterChange("timeInterval", option)}
                    options={timeIntervalOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    placeholder="Hourly"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-map-marker">
                  <Select
                    value={location}
                    onChange={(option) => handleFilterChange("location", option)}
                    options={locationOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="All Locations"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-user">
                  <Select
                    value={staff}
                    onChange={(option) => handleFilterChange("staff", option)}
                    options={staffOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    isLoading={loadingStaff}
                    placeholder="All Staff"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-desktop">
                  <Select
                    value={device}
                    onChange={(option) => handleFilterChange("device", option)}
                    options={deviceOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    isLoading={loadingDevices}
                    placeholder="All Devices"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill iconClass="fa fa-list">
                  <Select
                    value={reportingCategory}
                    onChange={(option) => handleFilterChange("reportingCategory", option)}
                    options={reportingCategoryOptions}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    isLoading={loadingCategories}
                    placeholder="All Categories"
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
              </>
            }
            headerControls={
              <ColumnSelection
                pageKey={pageKey}
                visibleColumns={visibleColumns}
                defaultVisibleColumns={defaultVisibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {salesData.length > 0 && (
                  <div
                    className="table-responsive mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 5,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <SortableColumnHeader
                            sortField={TIME_SORT_FIELDS.hourCommencing}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                          >
                            HOUR COMMENCING
                          </SortableColumnHeader>
                          {visibleColumns.transactionQty && (
                            <SortableColumnHeader
                              sortField={TIME_SORT_FIELDS.transactionQty}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              NUMBER OF ORDERS
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.refundQty && <th className="pt-2 pb-2">REFUND QTY</th>}
                          {visibleColumns.refundValue && <th className="pt-2 pb-2">REFUND VALUE</th>}
                          {visibleColumns.itemQty && <th className="pt-2 pb-2">ITEM QTY</th>}
                          {visibleColumns.salesIncTax && (
                            <SortableColumnHeader
                              sortField={TIME_SORT_FIELDS.salesIncTax}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              SALES INC. TAX
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.discountsPromotions && (
                            <th className="pt-2 pb-2">DISCOUNTS &amp; PROMOTIONS</th>
                          )}
                          {visibleColumns.avgTransactionNetSales && (
                            <th className="pt-2 pb-2">AVERAGE TRANSACTION NET SALES</th>
                          )}
                          {visibleColumns.netSalesIncTax && (
                            <SortableColumnHeader
                              sortField={TIME_SORT_FIELDS.netSalesIncTax}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              NET SALES INC. TAX
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.netSalesExcTax && (
                            <th className="pt-2 pb-2">NET SALES EXC. TAX</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {salesData.map((row, index) => (
                          <tr key={`${row.hourCommencing}-${index}`}>
                            <td>{formatHourCommencing(row.hourCommencing)}</td>
                            {visibleColumns.transactionQty && <td>{row.transactionQty}</td>}
                            {visibleColumns.refundQty && <td>{row.refundQty}</td>}
                            {visibleColumns.refundValue && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.refundValue.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.itemQty && <td>{row.itemQty}</td>}
                            {visibleColumns.salesIncTax && (
                              <td style={{ color: "#0d6efd" }}>
                                {getCurrencySymbol(compCurrency)} {row.salesIncTax.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.discountsPromotions && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.discountsPromotions.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.avgTransactionNetSales && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.avgTransactionNetSales.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.netSalesIncTax && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.netSalesIncTax.toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.netSalesExcTax && (
                              <td>
                                {getCurrencySymbol(compCurrency)} {row.netSalesExcTax.toFixed(2)}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

    </div>
  );
};

export default SalesBreakdownByTime;

